import React, { useState, useEffect } from "react";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { FaUserPlus, FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminParents = () => {
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [formData, setFormData] = useState({
    phonenumber: "",
    name: "",
    email: "",
  });
  const [editingParent, setEditingParent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // 退会済み／関連生徒なしの保護者一覧表示切替用状態
  const [showInactive, setShowInactive] = useState(true);
  const [formError, setFormError] = useState("");

  // メール形式のチェック（入力が任意の場合のみチェック）
  const isValidEmail = (str) => /\S+@\S+\.\S+/.test(str);

  // 保護者一覧の取得
  const fetchParents = async () => {
    try {
      const data = await getRequest("parents");
      if (data && data.parents) {
        setParents(data.parents);
      }
    } catch (error) {
      console.error("❌ 保護者データ取得エラー:", error);
    }
  };

  // 生徒一覧の取得
  const fetchStudents = async () => {
    try {
      const data = await getRequest("students");
      if (data && data.students) {
        setStudents(data.students);
      }
    } catch (error) {
      console.error("❌ 生徒データ取得エラー:", error);
    }
  };

  useEffect(() => {
    fetchParents();
    fetchStudents();
  }, []);

  const handleChange = (e) => {
    setFormError("");
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 入力値からハイフンを除去し、070,080,090で始まる11桁の携帯番号形式かチェック
  const validateAndCleanPhone = (phone) => {
    const cleanedPhone = phone.replace(/-/g, "");
    if (!/^(070|080|090)\d{8}$/.test(cleanedPhone)) {
      return null;
    }
    return cleanedPhone;
  };

  const addParent = async () => {
    setFormError("");
    setIsLoading(true);

    if (!formData.name.trim()) {
      setFormError("氏名は必須です。");
      setIsLoading(false);
      return;
    }

    const cleanedPhone = validateAndCleanPhone(formData.phonenumber);
    if (!cleanedPhone) {
      setFormError("携帯電話番号は070,080,090で始まる11桁の番号を入力してください。");
      setIsLoading(false);
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setFormError("メールアドレスの形式が不正です。");
      setIsLoading(false);
      return;
    }

    if (parents.find((p) => p.phonenumber === cleanedPhone)) {
      setFormError("同じ電話番号の保護者が既に登録されています");
      setIsLoading(false);
      return;
    }
    try {
      const response = await postRequest("parents", { ...formData, phonenumber: cleanedPhone });
      if (response.ok) {
        fetchParents();
        setFormData({ phonenumber: "", name: "", email: "" });
        toast.success("保護者登録が完了しました");
      } else {
        const data = await response.json();
        toast.error(data.error || "保護者追加に失敗しました");
        return;
      }
    } catch (error) {
      console.error("❌ 保護者追加エラー:", error);
      toast.error("保護者追加の通信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (parent) => {
    setEditingParent(parent.id);
    setFormError("");
    setFormData({
      phonenumber: parent.phonenumber,
      name: parent.name,
      email: parent.email,
    });
  };

  const updateParent = async () => {
    setFormError("");
    setIsLoading(true);

    if (!formData.name.trim()) {
      setFormError("氏名は必須です。");
      setIsLoading(false);
      return;
    }

    const cleanedPhone = validateAndCleanPhone(formData.phonenumber);
    if (!cleanedPhone) {
      setFormError("携帯電話番号は070,080,090で始まる11桁の番号を入力してください。");
      setIsLoading(false);
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setFormError("メールアドレスの形式が不正です。");
      setIsLoading(false);
      return;
    }

    if (parents.find((p) => p.phonenumber === cleanedPhone && p.id !== editingParent)) {
      toast.error("同じ電話番号の保護者が既に登録されています");
      setIsLoading(false);
      return;
    }
    try {
      const response = await putRequest(`parents/${editingParent}`, { ...formData, phonenumber: cleanedPhone });
      if (response.ok) {
        fetchParents();
        setEditingParent(null);
        setFormData({ phonenumber: "", name: "", email: "" });
        toast.success("保護者情報の更新が完了しました");
        setFormError("");
      } else {
        const data = await response.json();
        toast.error(data.error || "保護者情報の更新に失敗しました");
        return;
      }
    } catch (error) {
      console.error("❌ 保護者更新エラー:", error);
      toast.error("保護者情報更新の通信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  // 削除処理：deleteRequest を使い、サーバーからの403エラーの場合は理由をトーストで表示
  const deleteParent = async (id) => {
    try {
      const response = await deleteRequest(`parents/${id}`);
      if (response.ok) {
        fetchParents();
        toast.success("保護者が削除されました");
      } else {
        if (response.status == 403) {
          toast.error("この保護者の生徒が存在するため、削除できません");
        } else {
          toast.error("保護者削除に失敗しました");
        }
      }
    } catch (error) {
      console.error("❌ 保護者削除エラー:", error);
      toast.error("保護者削除の通信に失敗しました");
    }
  };

  // 指定した保護者IDに関連する生徒を取得
  const getStudentsForParent = (parentId) => {
    return students.filter((student) => student.parentid === parentId);
  };

  // 現役の生徒が1人以上いる保護者
  const activeParents = parents.filter((parent) => {
    const relatedStudents = getStudentsForParent(parent.id);
    return relatedStudents.some((student) => student.withdrawn === false);
  });

  // inactiveParents：関連生徒が存在しない、または全て退会済みの保護者
  const inactiveParents = parents.filter((parent) => {
    const relatedStudents = getStudentsForParent(parent.id);
    return relatedStudents.length === 0 || relatedStudents.every((student) => student.withdrawn === true);
  });

  // さらにinactiveParentsを「退会済み保護者」と「関連生徒なしの保護者」に分割
  const withdrawnParents = inactiveParents.filter((parent) => getStudentsForParent(parent.id).length > 0);
  const noRelatedParents = inactiveParents.filter((parent) => getStudentsForParent(parent.id).length === 0);

  return (
    <div className="p-6 pt-20 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">保護者管理</h1>

      {/* 個別登録／更新フォーム */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">
          {editingParent ? "保護者を編集" : "新しい保護者を追加"}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            name="phonenumber"
            value={formData.phonenumber}
            onChange={handleChange}
            placeholder="電話番号（例：07012345678 または 070-1234-5678）"
            className="p-2 border rounded"
          />
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="氏名"
            className="p-2 border rounded"
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="メールアドレス（任意）"
            className="p-2 border rounded"
          />
        </div>
        {formError && <p className="text-red-500 mt-2">{formError}</p>}
        <div className="flex gap-4 mt-3">
          {editingParent ? (
      <>
            <button
              onClick={updateParent}
              disabled={isLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
            >
              {isLoading ? "更新中…" : "更新"}
            </button>
        <button
          onClick={() => {
            // 編集モード解除とフォームの初期化
            setEditingParent(null);
            setFormData({ phonenumber: "", name: "", email: "" });
          }}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
        >
          キャンセル
        </button>
      </>
          ) : (
            <button
              onClick={addParent}
              disabled={isLoading}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
            >
              {isLoading ? "追加中…" : (<><FaUserPlus className="inline mr-2" />追加</>)}
            </button>
          )}
        </div>
      </div>

      {/* 現役の生徒がいる保護者一覧 */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">保護者一覧（現役の生徒あり）</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">電話番号</th>
              <th className="p-2 border">氏名</th>
              <th className="p-2 border">メール</th>
              <th className="p-2 border">生徒名</th>
              <th className="p-2 border">操作</th>
            </tr>
          </thead>
          <tbody>
            {activeParents.map((parent) => {
              const relatedActiveStudents = getStudentsForParent(parent.id).filter(
                (student) => student.withdrawn === false
              );
              return (
                <tr key={parent.id} className="border">
                  <td className="p-2 border">{parent.phonenumber}</td>
                  <td className="p-2 border">{parent.name}</td>
                  <td className="p-2 border">{parent.email}</td>
                  <td className="p-2 border">
                    {relatedActiveStudents.map((student) => student.name).join(", ")}
                  </td>
                  <td className="p-2 border flex gap-2">
                    <button
                      onClick={() => startEditing(parent)}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 退会済み保護者一覧 */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">退会済み保護者一覧</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">電話番号</th>
              <th className="p-2 border">氏名</th>
              <th className="p-2 border">メール</th>
              <th className="p-2 border">生徒名</th>
              <th className="p-2 border">操作</th>
            </tr>
          </thead>
          <tbody>
            {withdrawnParents.map((parent) => {
              const relatedNames = getStudentsForParent(parent.id)
                .map((student) => student.name)
                .join(", ");
              return (
                <tr key={parent.id} className="border">
                  <td className="p-2 border">{parent.phonenumber}</td>
                  <td className="p-2 border">{parent.name}</td>
                  <td className="p-2 border">{parent.email}</td>
                  <td className="p-2 border">{relatedNames || "なし"}</td>
                  <td className="p-2 border flex gap-2">
                    <button
                      onClick={() => startEditing(parent)}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 関連生徒なしの保護者一覧 */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">関連生徒なしの保護者一覧</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">電話番号</th>
              <th className="p-2 border">氏名</th>
              <th className="p-2 border">メール</th>
              <th className="p-2 border">操作</th>
            </tr>
          </thead>
          <tbody>
            {noRelatedParents.map((parent) => (
              <tr key={parent.id} className="border">
                <td className="p-2 border">{parent.phonenumber}</td>
                <td className="p-2 border">{parent.name}</td>
                <td className="p-2 border">{parent.email}</td>
                <td className="p-2 border flex gap-2">
                  <button
                    onClick={() => startEditing(parent)}
                    className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("本当に削除しますか？")) {
                        deleteParent(parent.id);
                      }
                    }}
                    className="bg-red-700 text-white px-3 py-1 rounded hover:bg-red-800"
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminParents;
